import type { AxiosInstance } from 'axios';
import type { Logger } from '@wg/api-client';
import type {
  BalanceResponse,
  Entitlement,
  EntitlementStatus,
  GrantTypeConfig,
  NotificationHistoryResponse,
  RedeemCodeResponse,
  RegisterDeviceRequest,
  RevenueCatSyncResponse,
  SendTestNotificationRequest,
  SessionDetails,
  TransactionFilters,
  TransactionsResponse,
  UpdateDeviceRequest,
  UsageEvent,
  UserDevice,
} from '@wg/types';

export type { Logger };

export interface BillingApi {
  getUserBalance(breakdown?: boolean): Promise<BalanceResponse>;
  checkEntitlement(featureCode?: string): Promise<EntitlementStatus>;
  getUserEntitlements(
    activeOnly?: boolean,
    featureCode?: string,
  ): Promise<Entitlement[]>;
  getUserTransactions(
    assetCode?: string,
    limit?: number,
    offset?: number,
    filters?: TransactionFilters,
  ): Promise<TransactionsResponse>;
  getUserSessions(
    limit?: number,
    offset?: number,
    minDuration?: number,
  ): Promise<SessionDetails[]>;
  getSessionUsage(sessionId: string): Promise<UsageEvent[]>;
  redeemCode(code: string, language?: string): Promise<RedeemCodeResponse>;
  syncRevenueCatSubscriptions(
    appUserId: string,
  ): Promise<RevenueCatSyncResponse>;
  getGrantTypeConfigs(lang?: string): Promise<GrantTypeConfig[]>;
  registerDevice(deviceData: RegisterDeviceRequest): Promise<UserDevice>;
  getUserDevices(): Promise<UserDevice[]>;
  updateDevice(
    deviceId: string,
    updates: UpdateDeviceRequest,
  ): Promise<UserDevice>;
  unregisterDevice(deviceId: string): Promise<void>;
  sendTestNotification(
    notification: SendTestNotificationRequest,
  ): Promise<void>;
  getNotificationHistory(params?: {
    notification_type?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<NotificationHistoryResponse>;
  markNotificationClicked(
    notificationId: string,
    deviceId: string,
  ): Promise<void>;
}

/**
 * Creates a billing API client backed by the supplied axios instance.
 *
 * The 401/404 patterns below intentionally swallow logging at info-level rather
 * than error — billing is feature-flagged off in some environments and we don't
 * want to spam error logs when it's simply disabled.
 */
export function createBillingApi(
  http: AxiosInstance,
  logger: Logger,
): BillingApi {
  const isBenignBillingError = (error: unknown) => {
    const status = (error as { response?: { status?: number } } | undefined)
      ?.response?.status;
    return status === 401 || status === 404;
  };

  return {
    async getUserBalance(breakdown = false) {
      try {
        const response = await http.get<BalanceResponse>(
          '/billing/v1/me/balance',
          {
            params: { asset_code: 'voice_seconds', breakdown },
          },
        );
        logger.debug('[BillingAPI] Balance retrieved', {
          balance: response.data.balance,
        });
        return response.data;
      } catch (error) {
        if (isBenignBillingError(error)) {
          logger.info('[BillingAPI] Billing service not available (401/404)');
        } else {
          logger.error('[BillingAPI] Failed to get balance', error);
        }
        throw error;
      }
    },

    async checkEntitlement(featureCode = 'voice_unmetered') {
      try {
        const response = await http.get<EntitlementStatus>(
          '/billing/v1/entitlements/me/status',
          {
            params: { feature_code: featureCode },
          },
        );
        logger.debug('[BillingAPI] Entitlement status checked', {
          featureCode,
          hasEntitlement: response.data.has_entitlement,
        });
        return response.data;
      } catch (error) {
        if (isBenignBillingError(error)) {
          logger.info('[BillingAPI] Billing service not available (401/404)');
        } else {
          logger.error('[BillingAPI] Failed to check entitlement', error);
        }
        throw error;
      }
    },

    async getUserEntitlements(activeOnly = true, featureCode) {
      try {
        const response = await http.get<{
          user_id: string;
          entitlements_count: number;
          entitlements: Entitlement[];
        }>('/billing/v1/entitlements/me/all', {
          params: { active_only: activeOnly, feature_code: featureCode },
        });
        logger.debug('[BillingAPI] Entitlements retrieved', {
          count: response.data.entitlements_count,
        });
        return response.data.entitlements;
      } catch (error) {
        logger.error('[BillingAPI] Failed to get entitlements', error);
        throw error;
      }
    },

    async getUserTransactions(
      assetCode = 'voice_seconds',
      limit = 50,
      offset = 0,
      filters,
    ) {
      try {
        const params: Record<string, unknown> = {
          asset_code: filters?.assetCode ?? assetCode,
          limit: filters?.limit ?? limit,
          offset: filters?.offset ?? offset,
        };
        if (filters?.transactionType)
          params.transaction_type = filters.transactionType;
        if (filters?.fromDate) params.from_date = filters.fromDate;
        if (filters?.toDate) params.to_date = filters.toDate;
        if (filters?.sourceTypeExclude)
          params.source_type_exclude = filters.sourceTypeExclude;

        const response = await http.get<TransactionsResponse>(
          '/billing/v1/me/transactions',
          {
            params,
          },
        );
        logger.debug('[BillingAPI] Transactions retrieved', {
          count: response.data.count,
          filters: filters ?? 'none',
        });
        return response.data;
      } catch (error) {
        logger.error('[BillingAPI] Failed to get transactions', error);
        throw error;
      }
    },

    async getUserSessions(limit = 20, offset = 0, minDuration) {
      try {
        const params: Record<string, unknown> = { limit, offset };
        if (minDuration !== undefined) params.min_duration = minDuration;
        const response = await http.get<SessionDetails[]>(
          '/billing/v1/sessions',
          { params },
        );
        logger.debug('[BillingAPI] Sessions retrieved', {
          count: response.data.length,
          minDuration: minDuration ?? 'none',
        });
        return response.data;
      } catch (error) {
        logger.error('[BillingAPI] Failed to get sessions', error);
        throw error;
      }
    },

    async getSessionUsage(sessionId) {
      try {
        const response = await http.get<UsageEvent[]>(
          `/billing/v1/sessions/${sessionId}/usage`,
        );
        logger.debug('[BillingAPI] Session usage retrieved', {
          sessionId,
          eventCount: response.data.length,
        });
        return response.data;
      } catch (error) {
        logger.error('[BillingAPI] Failed to get session usage', error);
        throw error;
      }
    },

    async redeemCode(code, language) {
      try {
        const response = await http.post<RedeemCodeResponse>(
          '/billing/v1/codes/redeem',
          { code: code.toUpperCase().trim() },
          { params: language ? { lang: language } : undefined },
        );
        logger.info('[BillingAPI] Code redeemed successfully', {
          code: code.substring(0, 4) + '****',
          creditGranted: response.data.granted.amount,
        });
        return response.data;
      } catch (error) {
        logger.error('[BillingAPI] Failed to redeem code', error);
        throw error;
      }
    },

    async syncRevenueCatSubscriptions(appUserId) {
      try {
        logger.info('[BillingAPI] Syncing RevenueCat subscriptions', {
          appUserId,
        });
        const response = await http.post<RevenueCatSyncResponse>(
          '/billing/v1/revenuecat/me/sync',
          { app_user_id: appUserId },
        );
        logger.info('[BillingAPI] RevenueCat sync successful', {
          appUserId,
          syncedEntitlements: response.data.synced_entitlements.length,
          syncedCredits: response.data.synced_credits.length,
          errors: response.data.errors.length,
        });
        return response.data;
      } catch (error) {
        if (isBenignBillingError(error)) {
          logger.info('[BillingAPI] Billing service not available (401/404)', {
            appUserId,
          });
        } else {
          logger.error('[BillingAPI] Failed to sync RevenueCat subscriptions', {
            appUserId,
            error,
          });
        }
        throw error;
      }
    },

    async getGrantTypeConfigs(lang = 'en') {
      try {
        const response = await http.get<GrantTypeConfig[]>(
          '/billing/v1/metadata/grant-types',
          {
            params: { lang },
          },
        );
        return response.data;
      } catch (error) {
        logger.error('[BillingAPI] Failed to fetch grant type configs', {
          error,
          lang,
        });
        throw error;
      }
    },

    async registerDevice(deviceData) {
      try {
        const response = await http.post<UserDevice>(
          '/notifications/devices',
          deviceData,
        );
        logger.info('Device registered successfully', {
          platform: deviceData.platform,
        });
        return response.data;
      } catch (error) {
        logger.error('Failed to register device', { error });
        throw error;
      }
    },

    async getUserDevices() {
      try {
        const response = await http.get<{
          devices: UserDevice[];
          total: number;
        }>('/notifications/devices');
        return response.data.devices;
      } catch (error) {
        logger.error('Failed to get user devices', { error });
        throw error;
      }
    },

    async updateDevice(deviceId, updates) {
      try {
        const response = await http.patch<UserDevice>(
          `/notifications/devices/${deviceId}`,
          updates,
        );
        logger.info('Device updated successfully', { deviceId });
        return response.data;
      } catch (error) {
        logger.error('Failed to update device', { error, deviceId });
        throw error;
      }
    },

    async unregisterDevice(deviceId) {
      try {
        await http.delete(`/notifications/devices/${deviceId}`);
        logger.info('Device unregistered successfully', { deviceId });
      } catch (error) {
        logger.error('Failed to unregister device', { error, deviceId });
        throw error;
      }
    },

    async sendTestNotification(notification) {
      try {
        await http.post('/notifications/send-to-me', notification);
        logger.info('Test notification sent successfully');
      } catch (error) {
        logger.error('Failed to send test notification', { error });
        throw error;
      }
    },

    async getNotificationHistory(params) {
      try {
        const response = await http.get<NotificationHistoryResponse>(
          '/notifications/history',
          {
            params,
          },
        );
        return response.data;
      } catch (error) {
        logger.error('Failed to get notification history', { error });
        throw error;
      }
    },

    async markNotificationClicked(notificationId, deviceId) {
      try {
        await http.post('/notifications/clicked', {
          notification_id: notificationId,
          device_id: deviceId,
        });
        logger.info('Notification marked as clicked', {
          notificationId,
          deviceId,
        });
      } catch (error) {
        logger.error('Failed to mark notification as clicked', { error });
        throw error;
      }
    },
  };
}

// ──────────────────────────────────────────────────────────────
// Pure utility functions (no http) — re-exported for convenience
// ──────────────────────────────────────────────────────────────

export function formatVoiceTime(seconds: number): string {
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    if (minutes > 0) return `${hours}h ${minutes}m`;
    return `${hours}h`;
  }
  return `${minutes}m`;
}

export const isLowBalance = (balance: number): boolean => balance < 300;
export const isCriticalBalance = (balance: number): boolean => balance < 60;
export const isBalanceDepleted = (balance: number): boolean => balance <= 0;
